import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { EnvelopeIcon, ArrowLeftIcon, ChevronLeftIcon, ChevronRightIcon, EyeIcon } from '@heroicons/react/24/outline';
import PageLayout from '../components/PageLayout';
import PageBanner from '../components/PageBanner';
import SEO from '../components/SEO';
import API, { trackView } from '../api';
import { useLanguage } from '../context/LanguageContext';

const BASE = process.env.REACT_APP_API_URL?.replace('/api', '') ?? 'http://localhost:5000';
const URL_RE = /(https?:\/\/[^\s]+)/g;

// Turns bare URLs within a line of text into clickable links, leaving the
// surrounding text untouched.
function linkify(text) {
  const parts = text.split(URL_RE);
  if (parts.length === 1) return text;
  return parts.map((part, i) => URL_RE.test(part) ? (
    <a key={i} href={part} target="_blank" rel="noopener noreferrer"
      className="text-primary underline decoration-primary/30 hover:text-accent hover:decoration-accent/50 transition-colors break-all">
      {part}
    </a>
  ) : <React.Fragment key={i}>{part}</React.Fragment>);
}

// Specs are entered as lightweight markdown in the admin: "## " starts a
// heading, "- " / "* " lines group into a bullet list, blank lines separate
// paragraphs, and everything else is prose (with URLs auto-linked).
function parseSpecBlocks(specs) {
  if (!specs) return [];
  const lines = specs.replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let paragraph = [];
  let bullets = [];

  const flushParagraph = () => {
    if (paragraph.length) { blocks.push({ type: 'p', text: paragraph.join(' ') }); paragraph = []; }
  };
  const flushBullets = () => {
    if (bullets.length) { blocks.push({ type: 'ul', items: bullets }); bullets = []; }
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { flushParagraph(); flushBullets(); continue; }
    if (line.startsWith('## ')) {
      flushParagraph(); flushBullets();
      blocks.push({ type: 'h', text: line.slice(3).trim() });
    } else if (line.startsWith('- ') || line.startsWith('* ')) {
      flushParagraph();
      bullets.push(line.slice(2).trim());
    } else {
      flushBullets();
      paragraph.push(line);
    }
  }
  flushParagraph();
  flushBullets();
  return blocks;
}

function SpecBlocks({ specs }) {
  const { t } = useLanguage();
  const blocks = parseSpecBlocks(specs);
  if (blocks.length === 0) return null;
  return (
    <div className="mb-8">
      <h2 className="section-title text-lg mb-4">{t('common.specifications')}</h2>
      {blocks.map((block, i) => {
        if (block.type === 'h') {
          return (
            <div key={i} className="mt-6 first:mt-0 mb-2">
              <h3 className="font-display text-base font-bold text-primary-dark">{block.text}</h3>
              <div className="w-8 h-0.5 bg-accent mt-1.5" />
            </div>
          );
        }
        if (block.type === 'ul') {
          return (
            <ul key={i} className="space-y-1.5 mb-3">
              {block.items.map((item, j) => (
                <li key={j} className="flex items-start gap-2.5 text-sm text-gray-600 leading-relaxed">
                  <span className="w-1.5 h-1.5 bg-accent shrink-0 mt-[7px]" />
                  <span>{linkify(item)}</span>
                </li>
              ))}
            </ul>
          );
        }
        return <p key={i} className="text-sm text-gray-600 leading-relaxed mb-3">{linkify(block.text)}</p>;
      })}
    </div>
  );
}

function ProductGallery({ images, name }) {
  const [active, setActive] = useState(0);
  useEffect(() => { setActive(0); }, [images]);

  if (images.length === 0) {
    return (
      <div className="aspect-[4/3] bg-gradient-to-br from-primary/10 to-gray-100 flex items-center justify-center">
        <span className="text-primary/30 text-8xl font-display font-bold">{name[0]}</span>
      </div>
    );
  }

  const go = (delta) => setActive(i => (i + delta + images.length) % images.length);

  return (
    <div>
      <div className="relative aspect-[4/3] bg-gray-100 overflow-hidden group">
        <img src={images[active]} alt={`${name} — image ${active + 1}`} className="w-full h-full object-cover" />
        {images.length > 1 && (
          <>
            <button onClick={() => go(-1)} aria-label="Previous image"
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-primary p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <ChevronLeftIcon className="w-4 h-4" />
            </button>
            <button onClick={() => go(1)} aria-label="Next image"
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-primary p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <ChevronRightIcon className="w-4 h-4" />
            </button>
            <span className="absolute bottom-2 right-2 bg-primary-dark/70 text-white text-xs px-2 py-0.5">
              {active + 1} / {images.length}
            </span>
          </>
        )}
      </div>
      {images.length > 1 && (
        <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
          {images.map((url, i) => (
            <button key={i} onClick={() => setActive(i)}
              className={`shrink-0 w-16 h-16 border-2 overflow-hidden transition-colors ${i === active ? 'border-accent' : 'border-transparent hover:border-gray-200'}`}>
              <img src={url} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ProductDetail() {
  const { t } = useLanguage();
  const { categorySlug, brandSlug, productSlug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const trackedId = useRef(null);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    API.get(`/products/${productSlug}`)
      .then(r => setProduct(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [productSlug]);

  // Fires once the real product id is known, and only once per id — guards
  // against both StrictMode's dev double-invoke and this effect re-running
  // for unrelated reasons.
  useEffect(() => {
    if (product?.id && trackedId.current !== product.id) {
      trackedId.current = product.id;
      trackView('product', product.id);
    }
  }, [product?.id]);

  const resolveUrl = (url) => url?.startsWith('/') ? `${BASE}${url}` : url;
  const gallery = product
    ? [...new Set([product.image_url, ...(product.images || [])].filter(Boolean))].map(resolveUrl)
    : [];
  const backTo = categorySlug && brandSlug ? `/products/${categorySlug}/${brandSlug}` : (categorySlug ? `/products/${categorySlug}` : '/products');
  const backLabel = product?.brand_name || product?.category_name || t('common.products');

  const structuredData = product ? {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description || undefined,
    image: gallery.length > 0 ? gallery : undefined,
    category: product.category_name || undefined,
    brand: product.brand_name ? { '@type': 'Brand', name: product.brand_name } : undefined,
  } : null;

  if (!loading && (notFound || !product)) {
    return (
      <PageLayout>
        <PageBanner en="OUR PRODUCTS" title={t('product.notFoundTitle')} breadcrumbs={[{ label: t('common.products'), to: '/products' }, { label: t('product.notFoundTitle') }]} />
        <section className="py-20 text-center">
          <p className="text-gray-400 text-lg mb-6">{t('product.notFoundMessage')}</p>
          <Link to={backTo} className="btn-primary text-sm">
            {t('product.backToProducts')}
          </Link>
        </section>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <SEO title={product?.name} description={product?.description} structuredData={structuredData} />
      <PageBanner en="OUR PRODUCTS"
        title={product?.name || ''}
        breadcrumbs={[
          { label: t('common.products'), to: '/products' },
          ...(categorySlug
            ? [{ label: product?.category_name || 'Category', to: `/products/${categorySlug}` }]
            : []),
          ...(brandSlug
            ? [{ label: product?.brand_name || t('common.brand'), to: `/products/${categorySlug}/${brandSlug}` }]
            : []),
          { label: product?.name || '' },
        ]}
      />

      <section className="pt-6 pb-20 lg:pb-28 bg-white">
        <div className="w-[90%] max-w-7xl mx-auto">
          <Link to={backTo}
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-primary transition-colors mb-8">
            <ArrowLeftIcon className="w-3.5 h-3.5" /> {t('common.backTo')} {backLabel}
          </Link>

          {loading ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 animate-pulse">
              <div className="aspect-[4/3] bg-gray-100" />
              <div className="space-y-4">
                <div className="h-6 bg-gray-100 w-1/3" />
                <div className="h-8 bg-gray-100 w-2/3" />
                <div className="h-24 bg-gray-100" />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <ProductGallery images={gallery} name={product.name} />

              {/* Details */}
              <div>
                <div className="flex items-center justify-between gap-3">
                  {product.brand_name ? (
                    <span className="text-xs text-accent font-semibold uppercase tracking-wider">{product.brand_name}</span>
                  ) : <span />}
                  {product.unique_view_count > 0 && (
                    <span className="flex items-center gap-1 text-xs text-gray-400">
                      <EyeIcon className="w-3.5 h-3.5" /> {product.unique_view_count} {product.unique_view_count === 1 ? t('product.view') : t('product.views')}
                    </span>
                  )}
                </div>
                <h1 className="text-2xl sm:text-3xl font-bold text-ink mt-1 mb-4">{product.name}</h1>

                {product.description && (
                  <p className="text-gray-600 leading-relaxed mb-8 whitespace-pre-line">{product.description}</p>
                )}

                <SpecBlocks specs={product.specs} />

                <Link to="/contact" className="btn-primary text-sm tracking-widest uppercase inline-flex items-center gap-2">
                  <EnvelopeIcon className="w-4 h-4" /> {t('product.enquireAboutThisProduct')}
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>
    </PageLayout>
  );
}
